import { Box, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useNatGateway } from "../../hooks/useNatGateway";
import {
  getDataCheckerSchedule,
  initDataCheckerTask,
  updateDataCheckerSchedule,
} from "../../services/aws/ecs";
import ActionButtons from "./components/ActionButtons";
import DataCheckerForm from "./components/DataCheckerForm";
import ScheduledCompaniesForm from "./components/ScheduledCompaniesForm";
import useCompanyNames from "./hooks/useCompanyNames";

function DataChecker() {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useAlert();
  const {
    isReady: natGatewayReady,
    isLoading: natGatewayLoading,
    checkStatus,
    createGateway,
  } = useNatGateway("scraper-natgw");
  const { companyNames } = useCompanyNames();
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduledCompanies, setScheduledCompanies] = useState([]);

  const [formData, setFormData] = useState({
    selectedCompanies: [],
    selectedQuarter: null,
  });

  const validateInputs = () => {
    if (formData.selectedCompanies.length === 0) {
      showAlert("error", "Please select a Company!");
      return false;
    }
    if (!formData.selectedQuarter) {
      showAlert("error", "Please select a Quarter!");
      return false;
    }
    return true;
  };

  const handleSubmitTask = async () => {
    if (!validateInputs()) {
      return;
    }

    setTaskSubmitting(true);

    try {
      const companyName = formData.selectedCompanies.join(",");
      const res = await initDataCheckerTask(companyName, formData.selectedQuarter);
      if (res.status === 200) {
        showAlert("success", "Data checker task submitted successfully!");
        setTimeout(() => {
          navigate("/tasks");
        }, 1000);
      }
    } catch (error) {
      showAlert("error", "Failed to submit data checker task!");
      setTaskSubmitting(false);
    }
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      const res = await updateDataCheckerSchedule(scheduledCompanies);
      if (res.status === 200) {
        showAlert("success", "Schedule saved successfully!");
      }
    } catch (error) {
      showAlert("error", "Failed to save schedule!");
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleCreateNatGateway = async () => {
    showAlert("info", "Creating NAT Gateway, please wait...");
    await createGateway();
  };

  const handleFormChange = (newFormData) => {
    setFormData(newFormData);
  };

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const res = await getDataCheckerSchedule();
        if (res.status === 200) {
          const data = await res.json();
          setScheduledCompanies(data.companyNames ?? []);
        }
      } catch (error) {
        showAlert("error", "Could not load scheduled companies.");
      }
    };
    loadSchedule();
  }, []);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        <Grid>
          <Title text={"DATA CHECKER"} sx={{ my: 5 }} />
        </Grid>
        <Grid size="grow">
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              mt: 5,
            }}
          >
            <ActionButtons
              onSubmit={handleSubmitTask}
              onCreateGateway={handleCreateNatGateway}
              natGatewayReady={natGatewayReady}
              natGatewayLoading={natGatewayLoading}
              taskSubmitting={taskSubmitting}
            />
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      <DataCheckerForm companyNames={companyNames} formData={formData} onFormChange={handleFormChange} />
      <ScheduledCompaniesForm
        companyNames={companyNames}
        selectedCompanies={scheduledCompanies}
        onCompaniesChange={setScheduledCompanies}
        onSave={handleSaveSchedule}
        saving={scheduleSaving}
      />
    </Layout>
  );
}

export default DataChecker;
